import { useState } from 'react';
import { Alert, Button, Card, Checkbox, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  buildNotificationsPatch,
  getNotifications,
  patchIsEmpty,
  setNotifications,
  type NotificationChanges,
  type PANotificationRow,
  type PANotifications,
  type PANotifyChannel,
} from '../api/notifications';
import { getNewsCategories, setNewsSubscription, type PANewsCategories } from '../api/news';
import { getContacts, type PAContacts } from '../api/profile';
import { formatMinor, parseMinor } from '../format';
import { QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import {
  notificationChannelLabel,
  notificationLockedLabel,
  notificationRowLabel,
} from '../money/labels';
import { errorText } from '../session/errorText';

/**
 * ============================================================================
 * «ОПОВЕЩЕНИЯ» — §18's matrix, its one amount, and the category checklist (F5, F6)
 * ============================================================================
 *
 * ⚠ THE WRITE IS SPARSE AND THAT IS THE WHOLE POINT OF THIS SCREEN'S EXISTENCE. The staff
 * route is a FULL REPLACEMENT over thirty-eight columns; a portal on that shape rendering
 * only §18's seven original rows would silently zero the six Amendment-01 families —
 * including `suspended`, the message that names the exact figure restoring his service. The
 * backend was built sparse to make that impossible, and this screen must not hand it a full
 * matrix anyway: `buildNotificationsPatch` emits ONLY the (row, channel) pairs he touched,
 * and `AlertsScreen.test.tsx` asserts the recorded body — because a full-matrix POST is
 * invisible to every rendering test (the double answers on the path and the screen re-renders
 * from the response).
 *
 * # The six rules
 *
 *  1. **THE ROWS ARE THE SERVER'S.** They are derived from the `notif_*` column set, so a
 *     fourteenth alert family appears here the moment its columns exist — and an unknown key
 *     renders AS THE KEY rather than being dropped, so a row this build has no Russian
 *     sentence for is still visible and still switchable.
 *  2. **EACH ROW HAS ONLY THE CHANNELS IT HAS.** `invoice` has ONE (e-mail): it is a
 *     document-automation subscription rather than a text alert. A rendered checkbox the
 *     server would refuse (`pa.notification_channel_unknown`) is the dead-control class one
 *     level down, so the boxes come from `row.channels` and never from a fixed triple.
 *  3. **A LOCKED ROW GETS NO CONTROL AT ALL — not a disabled one.** `editable: false` is
 *     §18's monthly-invoice condition and the write answers `409 pa.notification_locked`, so
 *     the state is rendered as TEXT with the reason beside it. An explanation is not a
 *     control.
 *  4. **THE THRESHOLD IS MINOR UNITS AND `parseMinor` IS WHAT CONVERTS IT.** «250» typed into
 *     the box is 250 ROUBLES, and posting 250 minor units would arm a warning at 2,50 — a
 *     warning that can never fire on any real account. That conversion is
 *     `format.ts`'s ONE arithmetic-free reader of a figure the subscriber typed.
 *  5. **THE CATEGORY CHECKLIST SAVES ITSELF, THE MATRIX SAVES ON «Сохранить».** Two
 *     behaviours on one screen, so each block says which it is. The category write is ONE
 *     boolean on ONE category by design (a full «here are my categories» body would zero
 *     every category the screen did not render), and there is nothing to batch it with.
 *  6. **A MANDATORY CATEGORY IS SHOWN FIXED, NOT HIDDEN.** §18 and §2.8 are explicit: he has
 *     to be able to SEE that the outage announcements reach him and that he cannot switch
 *     them off. This is the one place in this app that renders a `disabled` control, it is a
 *     BOUND expression, and it is not a dead lever: it truthfully reports a state that cannot
 *     change, which is exactly what the operator's «Нельзя отписаться» means. The server
 *     refuses the write in EITHER direction (`409 pa.news_category_mandatory`).
 *
 * # What is deliberately NOT here
 *
 *   - **§18's CONTACTS BLOCK.** It is the same two columns «Профиль» writes, and a second
 *     editor for one pair is two doors to one write. What IS here is the delivery
 *     PRECONDITION — an unconfirmed contact receives NOTHING — as one advisory line naming
 *     the section that fixes it. Not restated per checkbox: one fact, one place.
 *   - **THE MARKETING CONSENT.** Appendix N §3.13 decision 8: operator-entered, read-only in
 *     the portal — «must not build the obvious-looking toggle». No method on this surface can
 *     reach the column.
 *   - **A «this will not reach you» MARK PER CHECKBOX.** See the advisory line above.
 */
export function AlertsScreen() {
  const { t } = useTranslation();
  const matrix = usePAQuery<PANotifications>(() => getNotifications(), []);
  const categories = usePAQuery<PANewsCategories>(() => getNewsCategories(), []);
  /*
    The contacts read is ADVISORY and its failure is not this screen's failure: the matrix is
    perfectly usable without it, so a read that fails renders no banner rather than an error
    frame over a working screen. Stated because the opposite reading — «a missing banner means
    everything is confirmed» — is the one mistake available here, and it is why the banner is
    driven by a SUCCESSFUL read and never by the absence of one.
  */
  const contacts = usePAQuery<PAContacts>(() => getContacts(), []);

  return (
    <>
      <h1 className="pa-page-title">{t('alerts.title')}</h1>
      <p className="pa-page-lead">{t('alerts.lead')}</p>

      {contacts.query.status === 'ready' ? <ContactNotice contacts={contacts.query.data} /> : null}

      <Card className="pa-block" title={t('alerts.categoriesTitle')}>
        <p className="pa-field-hint">{t('alerts.categoriesHint')}</p>
        <QueryFrame query={categories.query} reload={categories.reload}>
          {(data) => <CategoryList data={data} reload={categories.reload} />}
        </QueryFrame>
      </Card>

      <Card className="pa-block" title={t('alerts.matrixTitle')}>
        <QueryFrame query={matrix.query} reload={matrix.reload}>
          {(data) => <Matrix fromServer={data} reload={matrix.reload} />}
        </QueryFrame>
      </Card>
    </>
  );
}

/**
 * The delivery precondition, said once.
 *
 * An UNCONFIRMED contact receives nothing — not a suspension notice, not a payment
 * confirmation, not a rotated password — so a matrix ticked on an unconfirmed channel is a
 * subscriber waiting for messages that will never be sent. The line names «Профиль», which is
 * the section that fixes it.
 */
function ContactNotice({ contacts }: { contacts: PAContacts }) {
  const { t } = useTranslation();
  const mobileReady = contacts.mobile.value !== '' && contacts.mobile.confirmed;
  const emailReady = contacts.email.value !== '' && contacts.email.confirmed;
  if (mobileReady && emailReady) return null;
  return (
    <Alert
      type="warning"
      showIcon
      message={t('alerts.contactsTitle')}
      description={t(
        !mobileReady && !emailReady
          ? 'alerts.contactsNone'
          : !mobileReady
            ? 'alerts.contactsNoMobile'
            : 'alerts.contactsNoEmail',
      )}
      style={{ marginBottom: 16 }}
    />
  );
}

/** §18's «Категории сообщений» — one immediate write per tick. See rules 5 and 6. */
function CategoryList({ data, reload }: { data: PANewsCategories; reload: () => void }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PANewsCategories | null>(null);
  const current = rows ?? data;
  const [busy, setBusy] = useState(0);
  const [error, setError] = useState<unknown>(null);

  const toggle = async (id: number, subscribed: boolean) => {
    setBusy(id);
    setError(null);
    try {
      const fresh = await setNewsSubscription(id, subscribed);
      // The SERVER's row replaces ours: `subscribed` is derived there, and a re-tick of the
      // same value keeps the row it already had — which no client rule can know.
      setRows({
        ...current,
        items: current.items.map((c) => (c.id === fresh.id ? fresh : c)),
      });
    } catch (err) {
      setError(err);
      // A refusal means our copy produced a control the server has just refused (a category
      // marked mandatory since the read, or deleted), so the honest response is to re-read.
      setRows(null);
      reload();
    } finally {
      setBusy(0);
    }
  };

  if (current.items.length === 0) {
    return <Alert type="info" showIcon message={t('alerts.noCategories')} />;
  }

  return (
    <>
      {error ? (
        <Alert type="error" showIcon message={errorText(error, t)} style={{ marginBottom: 16 }} />
      ) : null}
      <div className="pa-checklist">
        {current.items.map((category) => (
          <div className="pa-check-row" key={category.id} data-testid={`pa-news-cat-${category.id}`}>
            <Checkbox
              checked={category.subscribed}
              // RULE 6: FIXED rather than absent, and a BOUND expression rather than a literal.
              disabled={category.mandatory || busy === category.id}
              onChange={(e) => void toggle(category.id, e.target.checked)}
            >
              {category.name}
            </Checkbox>
            <span className="pa-check-note">
              {category.mandatory ? t('alerts.categoryMandatory') : null}
              {/* HIS numbers, not the operator's item count. Rendered because «есть ли там
                  что-нибудь для меня» is what decides whether he opens the section. */}
              {category.unread > 0
                ? t('alerts.categoryUnread', { count: category.unread })
                : t('alerts.categoryLive', { count: category.live })}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

/** The matrix, its threshold and the sparse Save. */
function Matrix({ fromServer, reload }: { fromServer: PANotifications; reload: () => void }) {
  const { t } = useTranslation();
  /*
    ⚠ THE SERVER'S LATEST ANSWER SHADOWS THE QUERY'S, and it is `null` until there is one —
    the same shape `ProfileScreen` uses and for the same reason: the write returns the whole
    fresh matrix, so re-reading after it would be a second request describing the same card at
    a different instant. No effect copies the query into state, because an effect that
    overwrote a just-written value would be invisible to every test asserting on the first
    render.
  */
  const [local, setLocal] = useState<PANotifications | null>(null);
  const matrix = local ?? fromServer;

  const [changes, setChanges] = useState<NotificationChanges>({});
  const [thresholdText, setThresholdText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [localError, setLocalError] = useState('');

  const amountRow = matrix.rows.find((r) => r.has_amount);
  // `null` means «untouched», so the box shows the SERVER's figure until he edits it.
  const thresholdValue =
    thresholdText === null ? formatMinor(matrix.balance_threshold) : thresholdText;

  const answerOf = (row: PANotificationRow, channel: PANotifyChannel): boolean => {
    const pending = changes[row.key]?.[channel];
    return pending === undefined ? row.enabled[channel] === true : pending;
  };

  const setAnswer = (row: PANotificationRow, channel: PANotifyChannel, value: boolean) => {
    setLocalError('');
    setNotice('');
    setChanges((previous) => ({
      ...previous,
      [row.key]: { ...(previous[row.key] ?? {}), [channel]: value },
    }));
  };

  const save = async () => {
    let threshold: number | null = null;
    if (thresholdText !== null && thresholdText !== formatMinor(matrix.balance_threshold)) {
      // RULE 4: roubles typed → MINOR units on the wire. `null` from `parseMinor` means «this
      // is not a number», never zero, and zero is a REAL configured value here.
      const parsed = parseMinor(thresholdText);
      if (parsed === null) {
        setLocalError(t('alerts.thresholdInvalid'));
        return;
      }
      threshold = parsed;
    }
    const body = buildNotificationsPatch(changes, threshold);
    if (patchIsEmpty(body)) {
      // No request at all: a POST that writes nothing is a POST that reads to the operator's
      // audit log as a change he then has to explain.
      setNotice(t('alerts.nothingToSave'));
      return;
    }
    setBusy(true);
    setError(null);
    setNotice('');
    try {
      const fresh = await setNotifications(body);
      setLocal(fresh);
      setChanges({});
      setThresholdText(null);
      setNotice(t('alerts.saved'));
    } catch (err) {
      setError(err);
      // ⚠ THE WHOLE REQUEST IS REFUSED OR NONE OF IT IS, so his boxes are the only wrong
      // thing on the page. Two of the four refusals mean our MATRIX is stale — a row we do
      // not have, a channel that row does not have, or a lock that came into force since the
      // read — and for those the honest answer is to drop our copy and re-read; for a bad
      // THRESHOLD he can simply correct the number, so nothing is thrown away.
      if (isStaleMatrix(err)) {
        setChanges({});
        setThresholdText(null);
        setLocal(null);
        reload();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {notice ? (
        <Alert type="success" showIcon message={notice} style={{ marginBottom: 16 }} />
      ) : null}
      {error ? (
        <Alert type="error" showIcon message={errorText(error, t)} style={{ marginBottom: 16 }} />
      ) : null}

      {matrix.rows.length === 0 ? <Alert type="info" showIcon message={t('alerts.noRows')} /> : null}

      <div className="pa-matrix">
        {matrix.rows.map((row) => (
          <div className="pa-matrix-row" key={row.key} data-testid={`pa-alert-row-${row.key}`}>
            <span className="pa-matrix-label">{notificationRowLabel(row.key, t)}</span>

            {row.editable ? (
              <div className="pa-matrix-cells">
                {row.channels.map((channel) => (
                  <Checkbox
                    key={channel}
                    checked={answerOf(row, channel)}
                    onChange={(e) => setAnswer(row, channel, e.target.checked)}
                  >
                    {notificationChannelLabel(channel, t)}
                  </Checkbox>
                ))}
              </div>
            ) : (
              /* RULE 3: the STATE as text, and the reason. No control, not even a dead one. */
              <div className="pa-matrix-cells">
                {row.channels.map((channel) => (
                  <span className="pa-matrix-fixed" key={channel}>
                    {t(row.enabled[channel] ? 'alerts.channelOn' : 'alerts.channelOff', {
                      channel: notificationChannelLabel(channel, t),
                    })}
                  </span>
                ))}
                <span className="pa-check-note">{notificationLockedLabel(row.locked_reason, t)}</span>
              </div>
            )}

            {row.has_amount ? (
              <div className="pa-inline-form">
                <label className="pa-field-label" htmlFor="pa-alerts-threshold">
                  {t('alerts.thresholdLabel')}
                </label>
                <Input
                  id="pa-alerts-threshold"
                  className="pa-amount-input"
                  size="large"
                  inputMode="decimal"
                  value={thresholdValue}
                  status={localError ? 'error' : undefined}
                  onChange={(e) => {
                    setLocalError('');
                    setNotice('');
                    setThresholdText(e.target.value);
                  }}
                />
                <p className="pa-field-hint">{t('alerts.thresholdHint')}</p>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {localError ? <p className="pa-field-error">{localError}</p> : null}

      <div className="pa-actions">
        <Button type="primary" size="large" block loading={busy} onClick={() => void save()}>
          {t('alerts.save')}
        </Button>
      </div>

      {amountRow ? <p className="pa-field-hint">{t('alerts.thresholdNote')}</p> : null}
    </>
  );
}

/**
 * isStaleMatrix reports the three refusals that mean «the matrix you are holding is not the
 * one the server has».
 *
 * `pa.invalid_threshold` is deliberately NOT one of them: it is a mistake in the box, and
 * throwing away his other changes to correct a number would be the screen punishing him for
 * a typo.
 */
function isStaleMatrix(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code ?? '';
  return (
    code === 'pa.notification_row_unknown' ||
    code === 'pa.notification_channel_unknown' ||
    code === 'pa.notification_locked'
  );
}
